import React from "react";

/**
 * A 1-5 star control. Read-only by default; pass onChange to make it settable.
 *
 * Clicking the star that is already selected clears the rating - without that there is no way back
 * to "not rated" once a star is touched by accident, and a stray click would quietly enter the
 * average as a real opinion.
 */
export default function StarRating({ value, onChange, size = "1rem", disabled = false, label }) {
  const current = Number(value || 0);
  const readOnly = typeof onChange !== "function" || disabled;

  return (
    <span className="d-inline-flex align-items-center gap-1" role={readOnly ? "img" : "radiogroup"} aria-label={label}>
      {[1, 2, 3, 4, 5].map((star) => {
        const filled = star <= current;
        const icon = (
          <i
            className={`bi ${filled ? "bi-star-fill" : "bi-star"}`}
            style={{ fontSize: size, color: filled ? "#f0a500" : "#c7cdd4" }}
            aria-hidden="true"
          />
        );
        if (readOnly) return <span key={star}>{icon}</span>;
        return (
          <button
            key={star}
            type="button"
            className="btn btn-link p-0 border-0 lh-1"
            style={{ lineHeight: 1 }}
            aria-label={`${star} star${star === 1 ? "" : "s"}`}
            aria-pressed={filled}
            onClick={() => onChange(current === star ? null : star)}
          >
            {icon}
          </button>
        );
      })}
    </span>
  );
}

/** "4.3 ★ (12 trips)", or a plain "Not rated" - never a bare average with nothing behind it. */
export function RatingSummary({ average, ratedTrips, size = "0.9rem" }) {
  const trips = Number(ratedTrips || 0);
  if (!trips || average == null) {
    return <span className="text-muted small">Not rated</span>;
  }
  return (
    <span className="d-inline-flex align-items-center gap-1">
      <StarRating value={Math.round(Number(average))} size={size} label={`${average} out of 5`} />
      <span className="small fw-semibold">{Number(average).toFixed(1)}</span>
      <span className="small text-muted">
        ({trips} trip{trips === 1 ? "" : "s"})
      </span>
    </span>
  );
}
